/**
 * Production-ready speed test implementation
 * Optimized for low-speed connections with adaptive testing
 * Uses Cloudflare's reliable CDN endpoints
 */

import { downloadConfig, uploadConfig } from "../constants/constants";

type SpeedTestProgress =
    | {
          type: 'download' | 'upload';
          speed: string;
          progress: number;
          testIndex: number;
          totalTests: number;
          label: string;
          completed?: boolean;
      }
    | {
          phase: 'starting' | 'download' | 'upload' | 'completed' | 'error';
          message: string;
          results?: {
              download: string;
              upload: string;
              timestamp: string;
          };
          error?: string;
      };

interface SpeedTestOptions {
    onProgress?: (event: SpeedTestProgress) => void;
    skipUpload?: boolean;
}

// Cloudflare speed test base URL — configurable via env.
// For CRA: set `REACT_APP_CLOUDFLARE_SPEED_URL`.
// For Vite: set `VITE_CLOUDFLARE_SPEED_URL`.
const CLOUDFLARE_BASE =
    (typeof process !== 'undefined' && (process.env?.REACT_APP_CLOUDFLARE_SPEED_URL )) ||
    'https://speed.cloudflare.com';

const bytesToMegabits = (bytes: number): number => (bytes * 8) / 1_000_000;
const bytesToMegabytes = (bytes: number): number => bytes / 1_000_000;

/**
 * Measures download speed with streaming and progressive file sizing
 * @param {Function} onProgress - Callback for real-time progress updates
 * @returns {Promise<number>} Download speed in Mbps
 */
const measureDownloadSpeed = async (onProgress?: SpeedTestOptions['onProgress']): Promise<number> => {
    // Progressive test sizes - starts small, scales based on speed

    let bestSpeed = 0;
    let testIndex = 0;
    let config = downloadConfig;
    for (let k = 0; k < config.numberOfRetries; k++) {
        try {
            if( testIndex > 0 ) {
                onProgress?.({
                    message: `Retry count for download test  : ${testIndex + 1}`,
                    phase: 'download',
                    type: 'download',
                })
            }
            const url = `${CLOUDFLARE_BASE}/__down?bytes=${config.size}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), config.timeout);

            const startTime = performance.now();
            const response = await fetch(url, { 
                signal: controller.signal,
                cache: 'no-store'
            });

            if (!response.ok) throw new Error('Download failed');

            const reader = response.body?.getReader();
            if (!reader) throw new Error('Download stream unavailable');

            let receivedBytes = 0;
            const chunks: Uint8Array[] = [];

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                chunks.push(value);
                receivedBytes += value.length;

                const elapsed = (performance.now() - startTime) / 1000;
                const currentSpeed = bytesToMegabits(receivedBytes) / elapsed;

                onProgress?.({
                    type: 'download',
                    speed: currentSpeed.toFixed(2),
                    progress: (receivedBytes / config.size) * 100,
                    testIndex: testIndex + 1,
                    totalTests: config.numberOfRetries,
                    label: `Download test ${(bytesToMegabytes(config.size)).toFixed(2)} MB`
                });
            }

            clearTimeout(timeoutId);

            const totalTime = (performance.now() - startTime) / 1000;
            const measuredSpeed = bytesToMegabits(receivedBytes) / totalTime;
            bestSpeed = measuredSpeed;

            onProgress?.({
                type: 'download',
                speed: measuredSpeed.toFixed(2),
                progress: 100,
                testIndex: testIndex + 1,
                totalTests: config.numberOfRetries,
                label: `Download test ${(bytesToMegabytes(config.size)).toFixed(2)} MB`,
                completed: true
            });
            if( measuredSpeed >= config.thresholdSpeed ) {
                break; // Stop if we have a valid speed
            } else {
                console.log(`Download speed ${measuredSpeed.toFixed(2)} Mbps is below threshold value ${bytesToMegabytes(config.size).toFixed(2)} MB, retrying...`);
            }
    
        } catch (error) {
            testIndex++;
            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Download test timeout at ${bytesToMegabytes(config.size).toFixed(2)} MB`);
            }
        }
    }

    return bestSpeed;
};

/**
 * Measures upload speed with adaptive sizing
 * @param {Function} onProgress - Callback for real-time progress updates
 * @returns {Promise<number>} Upload speed in Mbps
 */
const measureUploadSpeed = async (onProgress?: SpeedTestOptions['onProgress']): Promise<number> => {
    

    let bestSpeed = 0;
    let testIndex = 0;
    let config = uploadConfig;
    for (let k = 0; k < config.numberOfRetries; k++) {
        try {
            if( testIndex > 0 ) {
                onProgress?.({
                    message: `Retry count for upload test  : ${testIndex + 1}`,
                    phase: 'upload',
                    type: 'upload',
                })
            }
            const data = new Uint8Array(config.size);
            const url = `${CLOUDFLARE_BASE}/__up`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), config.timeout);

            onProgress?.({
                type: 'upload',
                speed: '0.00',
                progress: 0,
                testIndex: testIndex + 1,
                totalTests: config.numberOfRetries,
                label: `Upload test ${(bytesToMegabytes(config.size)).toFixed(2)} MB`
            });

            const startTime = performance.now();

            const response = await fetch(url, {
                method: 'POST',
                body: data,
                signal: controller.signal,
                cache: 'no-store',
            });

            if (!response.ok) throw new Error('Upload failed');

            clearTimeout(timeoutId);

            const totalTime = (performance.now() - startTime) / 1000;
            const measuredSpeed = bytesToMegabits(config.size) / totalTime;
            bestSpeed = measuredSpeed;

            onProgress?.({
                type: 'upload',
                speed: measuredSpeed.toFixed(2),
                progress: 100,
                testIndex: testIndex + 1,
                totalTests: config.numberOfRetries,
                label: `Upload test ${(bytesToMegabytes(config.size)).toFixed(2)} MB`,
                completed: true
            });

            if ( measuredSpeed >= config.thresholdSpeed ) {
                console.log(`Upload speed ${measuredSpeed.toFixed(2)} Mbps is above threshold value ${bytesToMegabytes(config.size).toFixed(2)} MB, stopping retries.`);
                break; // Stop if we have a valid speed
            } else {
                console.log(`Upload speed ${measuredSpeed.toFixed(2)} Mbps is below threshold value ${bytesToMegabytes(config.size).toFixed(2)} MB, retrying...`);
            }
        } catch (error) {
            testIndex++;
            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Upload test timeout at ${bytesToMegabytes(config.size).toFixed(2)} MB`);
            }
        }
    }

    return bestSpeed;
};

/**
 * Runs complete speed test with download and upload measurements
 * @param {Object} options - Test configuration options
 * @param {Function} options.onProgress - Progress callback
 * @param {boolean} options.skipUpload - Skip upload test (default: false)
 * @returns {Promise<Object>} Test results { download, upload }
 */
export const speedtest = async (options: SpeedTestOptions = {}) => {
    const { onProgress, skipUpload = false } = options;

    try {
        onProgress?.({ phase: 'starting', message: 'Initializing speed test...' });

        // Wait 1 second to let connection stabilize
        await new Promise(resolve => setTimeout(resolve, 1000));

        onProgress?.({ phase: 'download', message: 'Testing download speed...' });
        const downloadSpeed = await measureDownloadSpeed(onProgress);

        let uploadSpeed = 0;
        if (!skipUpload) {
            // Wait 2 seconds between tests
            await new Promise(resolve => setTimeout(resolve, 2000));

            onProgress?.({ phase: 'upload', message: 'Testing upload speed...' });
            uploadSpeed = await measureUploadSpeed(onProgress);
        }

        const results = {
            download: downloadSpeed.toFixed(2),
            upload: uploadSpeed.toFixed(2),
            timestamp: new Date().toISOString()
        };

        onProgress?.({ 
            phase: 'completed', 
            message: 'Speed test completed',
            results 
        });

        console.log('Speed test results:', results);
        return results;

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('Speed test failed:', error);
        onProgress?.({
            phase: 'error',
            message: 'Speed test failed',
            error: errorMessage
        });
        
        return {
            download: 0.00,
            upload: 0.00,
            error: errorMessage
        };
    }
};



export default speedtest;
